import SectionCard from '../../components/ui/SectionCard'
import LoadingState from '../../components/ui/LoadingState'
import ErrorState from '../../components/ui/ErrorState'
import EmptyState from '../../components/ui/EmptyState'
import ExchangeRateHealthBadge from './ExchangeRateHealthBadge'
import ExchangeRatePaginationControls from './ExchangeRatePaginationControls'

const COLUMNS = [
  { key: 'baseCurrency', label: 'Base' },
  { key: 'quoteCurrency', label: 'Quote' },
  { key: 'rateDecimal', label: 'Rate' },
  { key: 'rateDate', label: 'Rate date' },
  { key: 'source', label: 'Source' },
  { key: 'sourcePriority', label: 'Priority' },
  { key: 'fetchedAt', label: 'Fetched at' },
  { key: 'status', label: 'Status' },
  { key: 'freshness', label: 'Freshness' },
  { key: 'isDerived', label: 'Derived' },
  { key: 'validFrom', label: 'Valid from' },
  { key: 'validTo', label: 'Valid to' },
]

function textOrDash(value) {
  if (value === undefined || value === null || value === '') return '—'
  return String(value)
}

export default function ExchangeRatePairsTable({
  pairs,
  loading,
  error,
  sort,
  onSortChange,
  onRetry,
  onPageChange,
  onPageSizeChange,
}) {
  const rows = Array.isArray(pairs?.rows) ? pairs.rows : []

  if (loading && !pairs) {
    return <LoadingState text="Loading pairs..." />
  }

  if (error && !pairs) {
    return <ErrorState text={error} action={<button type="button" className="underline" onClick={onRetry}>Retry</button>} />
  }

  if (rows.length === 0) {
    return <EmptyState title="No pair rows" description="No rows match current filters." />
  }

  return (
    <SectionCard title="Pairs" subtitle="Latest pair rows for audit">
      <div className="overflow-x-auto rounded-xl border border-outline bg-surface">
        <table className="min-w-full text-sm" aria-label="Exchange rate pairs">
          <thead className="bg-table-header">
            <tr>
              {COLUMNS.map((column) => {
                const ariaSort = sort?.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                return (
                  <th key={column.key} aria-sort={ariaSort} className="px-3 py-2 text-left font-semibold text-foreground">
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => onSortChange(column.key)}>
                      {column.label}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-outline">
            {rows.map((row, index) => (
              <tr key={`${row.baseCurrency}-${row.quoteCurrency}-${index}`}>
                <td className="px-3 py-2">{textOrDash(row.baseCurrency)}</td>
                <td className="px-3 py-2">{textOrDash(row.quoteCurrency)}</td>
                <td className="px-3 py-2">{textOrDash(row.rateDecimal)}</td>
                <td className="px-3 py-2">{textOrDash(row.rateDate)}</td>
                <td className="px-3 py-2">{textOrDash(row.source)}</td>
                <td className="px-3 py-2">{textOrDash(row.sourcePriority)}</td>
                <td className="px-3 py-2">{textOrDash(row.fetchedAt)}</td>
                <td className="px-3 py-2"><ExchangeRateHealthBadge health={row.status} /></td>
                <td className="px-3 py-2"><ExchangeRateHealthBadge health={row.freshness} /></td>
                <td className="px-3 py-2">{row.isDerived ? 'Yes' : 'No'}</td>
                <td className="px-3 py-2">{textOrDash(row.validFrom)}</td>
                <td className="px-3 py-2">{textOrDash(row.validTo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ExchangeRatePaginationControls
          page={pairs?.page}
          pageSize={pairs?.pageSize}
          total={pairs?.total}
          totalPages={pairs?.totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          itemLabel="pairs"
        />
      </div>
    </SectionCard>
  )
}
